/**
 * @module app.cm
 */

import { autoinject, computedFrom } from 'aurelia-framework';
import { IApiSettings } from 'app/shared/lib/api/api-types';
import { CaseWithLocalState } from 'app/cm/models/local-state-extensions';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { CaseService } from 'app/cm/services/case-service';
import { apiCall } from 'app/shared/lib/api/api';
import { caseIsReadOnly } from 'app/cm/lib/utilities';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { IQueueNavigationBinding } from 'app/core/models/essette-interfaces';
import { CustomFieldService } from 'app/core/services/custom-field-service';
import { deepClone } from 'app/shared/lib/utilities';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class CasePageContext {

    ////****************** Fields and Properties **************************************////
    public case: CaseWithLocalState = {};
    public queueBinding: IQueueNavigationBinding | null = null;
    public settings: IApiSettings = {};

    public caseCustomFieldTarget: Core.CustomFieldTarget | null = null;
    public goalCustomFieldTarget: Core.CustomFieldTarget | null = null;

    @computedFrom('case')
    public get isCaseLoaded(): boolean {
        return (this.case && !!this.case.id);
    }

    public caseIsReadOnly(canEditClosedCases?: boolean): boolean {
        if(!this.case) { this.logger.warn(`Context has no case loaded`); }
        const readonly = caseIsReadOnly(this.case, this.env.user.id);
        return readonly ?? true;
    }


    ////****************** Lifecycle ***********************************************////
    constructor(
        private readonly caseService: CaseService,
        private readonly env: EnvironmentService,
        private readonly customFieldService: CustomFieldService,
        private readonly logger: AppLogger) {

        this.logger.source = this;
    }


    ////****************** Functions ***********************************************////
    public clear(): void {
        this.case = {};
        this.settings = {};
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadCase(caseId: string, includes: string[] = [], fields: string[] = []): Promise<CM.Case> {
        try {
            // Push necessary fields for context case
            fields.push('isClosed');

            const response = await this.caseService.loadCase(caseId, includes, fields);
            this.case = response.results[0];
            await this.loadCustomFieldTargets();
            if(this.case.custom && !this.case.custom.target && this.caseCustomFieldTarget) {
                this.case.custom.target = deepClone(this.caseCustomFieldTarget);
            }
            this.logger.debug(`CaseContext loaded case ${this.case.number} (${this.case.id})`);
            return this.case;
        } catch(error) {
            throw error;
        }
    }

    @apiCall
    public async loadCustomFieldTargets(): Promise<void> {
        if(!this.case || (this.caseCustomFieldTarget && this.goalCustomFieldTarget)) return;

        try {
            const includes = [
                'definitions',
                'definitions.choices'
            ];

            const fields = [
                'definitions.choices.description',
                'definitions.description',
                'definitions.property.field',
                'definitions.property.type'
            ];

            const [caseCustomFieldTargetsResponse, goalCustomFieldTargetsResponse] = await Promise.all([
                await this.customFieldService.loadCustomFieldTargets({ targetType: 'CM.Case' }, { top: 1 }, includes, fields),
                await this.customFieldService.loadCustomFieldTargets({ targetType: 'CM.CarePlanGoal' }, { top: 1 }, includes, fields)
            ]);

            this.caseCustomFieldTarget = caseCustomFieldTargetsResponse.results[0] || null;
            this.goalCustomFieldTarget = goalCustomFieldTargetsResponse.results[0] || null;
        } catch(error) {
            throw error;
        }
    }

    @apiCall
    public async loadSettings(): Promise<IApiSettings> {
        try {
            const response = await this.caseService.loadCaseSettings();
            this.settings = response.settings;
            return this.settings;
        } catch(error) {
            throw error;
        }
    }
}
